import React, { useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  styled,
  tableCellClasses,
  TablePagination,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

// Styled Components
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#2C5F8E",
    color: "#FFFFFF",
    fontSize: 14,
    fontWeight: 500,
    whiteSpace: "nowrap",
    fontFamily: "Geist, sans-serif",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    fontFamily: "Geist, sans-serif",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(even)": {
    backgroundColor: "#EBF2FF",
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
  "&:hover": {
    backgroundColor: "#D6E6F2",
    boxShadow: "0 0 5px 0 grey",
  },
}));

// Mock Data
const statData = [
  { title: "Total Clients", value: 50 },
  { title: "Active Clients", value: 40 },
  { title: "Inactive Clients", value: 5 },
  { title: "Total Jobs Posted", value: 103 },
];

const clientStats = [
  { name: "Sarah Johnson", jobs: 45, interviews: 38, hires: 21 },
  { name: "Michael Chen", jobs: 30, interviews: 25, hires: 18 },
  { name: "Emily Davis", jobs: 28, interviews: 22, hires: 15 },
];

const jobPostingData = [
  { month: "Jan", jobs: 25 },
  { month: "Feb", jobs: 30 },
  { month: "Mar", jobs: 35 },
  { month: "Apr", jobs: 40 },
  { month: "May", jobs: 38 },
  { month: "Jun", jobs: 42 },
  { month: "Jul", jobs: 45 },
  { month: "Aug", jobs: 43 },
  { month: "Sep", jobs: 40 },
  { month: "Oct", jobs: 37 },
  { month: "Nov", jobs: 33 },
  { month: "Dec", jobs: 28 },
];

// Components
const StatCard = React.memo(({ title, value }) => (
  <Card
    sx={{
      minHeight: 100,
      textAlign: "center",
      boxShadow: "none",
      border: "1px solid lightgrey",
      borderRadius: "20px",
    }}
  >
    <CardContent>
      <Typography variant="h6" fontWeight={600} fontFamily="Geist, sans-serif">
        {title}
      </Typography>
      <Typography variant="h5" fontWeight={600} fontFamily="Geist, sans-serif">
        {value}
      </Typography>
    </CardContent>
  </Card>
));

const ClientPerformanceTable = ({ data }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const paginatedData = data.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box>
      <TableContainer
        sx={{
          border: "1px solid transparent",
          borderRadius: "10px",
          maxWidth: "90vw",
          overflowX: "auto",
          margin: "auto",
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <StyledTableCell sx={{ width: "50px" }}>#</StyledTableCell>
              <StyledTableCell>Client Name</StyledTableCell>
              <StyledTableCell>Jobs Posted</StyledTableCell>
              <StyledTableCell>Interviews</StyledTableCell>
              <StyledTableCell>Hires</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((client, idx) => (
              <StyledTableRow key={idx}>
                <StyledTableCell>
                  {page * rowsPerPage + idx + 1}
                </StyledTableCell>
                <StyledTableCell>{client.name}</StyledTableCell>
                <StyledTableCell>{client.jobs}</StyledTableCell>
                <StyledTableCell>{client.interviews}</StyledTableCell>
                <StyledTableCell>{client.hires}</StyledTableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={data.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[25, 50, 75, 100]}
        sx={{
          fontSize: { xs: "12px", sm: "14px" },
          "& .MuiTablePagination-selectLabel": {
            fontSize: { xs: "12px", sm: "14px" },
            fontFamily: "Geist, sans-serif",
          },
        }}
      />
    </Box>
  );
};

const JobsPostedChart = ({ data }) => (
  <Paper sx={{ mt: 4, p: 3 }}>
    <Typography variant="h6" mb={2}>
      Jobs Posted Over Time
    </Typography>
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="month" />
        <YAxis />
        <Tooltip />
        <Bar dataKey="jobs" fill="#006CB8" />
      </BarChart>
    </ResponsiveContainer>
  </Paper>
);

// Main View
const ClientReportView = () => {
  return (
    <Box sx={{ p: 3 }}>
      <Typography
        variant="h5"
        sx={{ fontWeight: 600, fontFamily: "Geist, sans-serif" }}
        mb={2}
      >
        Client Reports
      </Typography>

      <Grid container spacing={2}>
        {statData.map((item, idx) => (
          <Grid item xs={12} sm={6} md={3} key={idx}>
            <StatCard title={item.title} value={item.value} />
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mt: 4 }}>
        <Typography
          variant="h5"
          sx={{ fontWeight: 600, fontFamily: "Geist, sans-serif" }}
          mb={2}
        >
          Top Performing Clients
        </Typography>
        <ClientPerformanceTable data={clientStats} />
      </Box>

      <JobsPostedChart data={jobPostingData} />
    </Box>
  );
};

export default ClientReportView;
